import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import AuthLayout from '../../../components/shared/AuthLayout/AuthLayout';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { FormikPhoneInput } from '../../../components/shared/FormikPhoneInput/FormikPhoneInput';
import { getNumberConfirmation, sendSkipMobileVerification } from '../../../redux/actions/authActions';
import urls from '../../../routing/urls';

import styles from './ConfirmPhoneNumber.module.scss';

const ConfirmPhoneNumber = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.authReducer);
  const { currentStep } = useSelector((state) => state.stepsReducer);
  const [state, setState] = useState({
    selectingPhone: true,
    showTooltip: false,
    isLoading: false,
    countryCode: 'us',
    isDisabled: true,
    recaptchaToken: null,
  });

  const validationSchema = Yup.object({
    phone_number: Yup.string()
      .required(t('validation:phone.required'))
      .matches(/^\+?[1-9]\d{1,14}$/, t('validation:phone.invalidFormat'))
      .min(10, t('validation:phone.minDigits', { min: 10 }))
      .max(15, t('validation:phone.maxDigits', { max: 15 })),
  });

  const initialValues = {
    phone_number: '',
  };

  const displayTooltip = () => {
    setState((prev) => ({ ...prev, showTooltip: true }));
  };

  const hideTooltip = () => {
    setState((prev) => ({ ...prev, showTooltip: false }));
  };

  const onCaptchaChange = (token) => {
    setState((prev) => ({
      ...prev,
      recaptchaToken: token,
      isDisabled: false,
    }));
  };

  const setButtonActive = () => setState((prev) => ({ ...prev, isDisabled: false }));

  const submitForm = (values) => {
    let phoneNumber = values.phone_number;
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = `+${phoneNumber}`;
    }

    const formData = {
      phone_number: phoneNumber,
      recaptchaToken: state.recaptchaToken,
      email: currentUser.user.email,
    };

    dispatch(
      getNumberConfirmation(formData, () => {
        setButtonActive();
        navigate(urls.confirmationCode);
      })
    );
  };

  const handleSetupLater = () => {
    dispatch(sendSkipMobileVerification());
    if (currentStep === 'location') {
      navigate(urls.location);
    }
  };

  const renderSignUpPhoneDescription = () => (
    <AuthLayout>
      <div className={styles.container}>
        <div className={styles.formWrapperToolTip}>
          <button className={styles.closeButton} onClick={hideTooltip} type="button">
            ✕
          </button>
          <div className={styles.descriptionWrapper}>
            <p className={styles.descriptionText}>
              {t('signUp.phoneSecurityPurpose')}
              <br />
              {t('signUp.standardRatesMayApply')}
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );

  const renderSignUpPhoneForm = () => {
    const { isLoading, recaptchaToken } = state;

    return (
      <AuthLayout>
        {isLoading ? (
          <div className={styles.spinner}>{t('loading')}</div>
        ) : (
          <div className={styles.container}>
            <div className={styles.formWrapper}>
              <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitForm}>
                {({ isValid }) => (
                  <Form className={styles.form}>
                    <button className={styles.helpButton} onClick={displayTooltip} type="button">
                      ?
                    </button>
                    <div className={styles.header}>
                      <h2 className={styles.headerText}>{t('signUp.enterPhoneNumber')}</h2>
                    </div>

                    <div className={styles.phoneInputWrapper}>
                      <FormikPhoneInput name="phone_number" isRequired isSignupFlow />
                    </div>

                    <div className={styles.recaptchaWrapper}>
                      <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={onCaptchaChange} />
                    </div>

                    <div className={styles.buttonWrapper}>
                      <ButtonWithIcon
                        imgClassName="btn-icon-36"
                        text={t('buttons.sendCode')}
                        textClassName="btn-icon-text-primary-40"
                        variant="primary-36"
                        disabled={!isValid || !recaptchaToken}
                        type="submit"
                      />
                    </div>

                    <div className={styles.centerAlign}>
                      <button className={styles.setupLaterButton} type="button" onClick={handleSetupLater}>
                        {t('signUp.setupLater')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
      </AuthLayout>
    );
  };

  const { showTooltip } = state;
  return showTooltip ? renderSignUpPhoneDescription() : renderSignUpPhoneForm();
};

export default ConfirmPhoneNumber;
